import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import kycService from "../../../services/api/kyc.service";

const DematDetailsForm = ({ onComplete }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    dpId: "",
    clientId: "",
    depository: "NSDL",
    dan: "", // This will be auto-generated
  });

  const [nsdlData, setNsdlData] = useState({ dpId: "", clientId: "" });
  const [cdslData, setCdslData] = useState({ dpId: "", clientId: "" });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    dpId: "",
    clientId: "",
    depository: "",
  });
  const [kycStatus, setKycStatus] = useState(null);

  // Auto-generate DAN when DP ID, Client ID, or Depository changes
  useEffect(() => {
    if (formData.dpId && formData.clientId && formData.depository) {
      let generatedDan = "";

      if (formData.depository === "NSDL") {
        // NSDL format: DP ID (IN + 6 digits, e.g. IN300123) + Client ID (8 digits)
        // Full 16-digit DAN = IN300123 + XXXXXXXX
        generatedDan = `${formData.dpId}${formData.clientId}`;
      } else if (formData.depository === "CDSL") {
        // CDSL format: DP ID (8 digits) + Client ID (8 digits) = 16 digits
        generatedDan = `${formData.dpId}${formData.clientId}`;
      }

      setFormData((prev) => ({
        ...prev,
        dan: generatedDan,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        dan: "",
      }));
    }
  }, [formData.dpId, formData.clientId, formData.depository]);

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setLoading(true);
        const profileData = await kycService.getProfile();

        if (profileData && profileData.kycDetails?.dematDetails) {
          const depo = profileData.kycDetails.dematDetails.depository || "NSDL";
          const dId = profileData.kycDetails.dematDetails.dpId || "";
          const cId = profileData.kycDetails.dematDetails.clientId || "";

          setFormData({
            dpId: dId,
            clientId: cId,
            depository: depo,
            dan: profileData.kycDetails.dematDetails.dan || "",
          });
          
          if (depo === "NSDL") {
            setNsdlData({ dpId: dId, clientId: cId });
          } else if (depo === "CDSL") {
            setCdslData({ dpId: dId, clientId: cId });
          }
        }
        // Set KYC lock status
        const status = profileData?.kycDetails?.verificationStatus || null;
        setKycStatus(status);
      } catch (error) {
        console.error("Error loading existing KYC data:", error);
        toast.error("Failed to load existing data");
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, []);

  const validateField = (name, value, currentDepository = formData.depository) => {
    let error = "";

    switch (name) {
      case "dpId":
        if (!value.trim()) {
          error = "DP ID is required";
        } else if (currentDepository === "NSDL") {
          // NSDL: must be exactly "IN" + 6 numeric digits = 8 chars total
          if (!/^IN\d{6}$/.test(value)) {
            error = 'NSDL DP ID must be "IN" followed by 6 digits (e.g. IN300123)';
          }
        } else {
          // CDSL: 8 numeric digits
          if (!/^\d{8}$/.test(value)) {
            error = "CDSL DP ID must be exactly 8 digits";
          }
        }
        break;
      case "clientId":
        if (!value.trim()) {
          error = "Client ID (BO ID) is required";
        } else if (!/^\d{8}$/.test(value)) {
          error = "Client ID must be exactly 8 digits";
        }
        break;
      case "depository":
        if (!value.trim()) {
          error = "Depository is required";
        } else if (!["NSDL", "CDSL"].includes(value)) {
          error = "Depository must be either NSDL or CDSL";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    // Don't allow manual editing of DAN field
    if (id === "dan") return;

    if (id === "depository") {
      // Save current input to the active depository's storage
      if (formData.depository === "NSDL") {
        setNsdlData({ dpId: formData.dpId, clientId: formData.clientId });
      } else {
        setCdslData({ dpId: formData.dpId, clientId: formData.clientId });
      }

      // Load new input from the target depository's storage
      const defaultData = value === "NSDL" ? nsdlData : cdslData;
      
      setFormData({
        ...formData,
        [id]: value,
        dpId: defaultData.dpId,
        clientId: defaultData.clientId,
        dan: "",
      });
      
      setValidationErrors({
        ...validationErrors,
        [id]: validateField(id, value, value),
        dpId: defaultData.dpId ? validateField("dpId", defaultData.dpId, value) : "",
        clientId: defaultData.clientId ? validateField("clientId", defaultData.clientId, value) : "",
      });
      return;
    }

    if (id === "dpId") {
      if (formData.depository === "NSDL") {
        // Handle NSDL: The prefix "IN" is now visual, we only store the 6 digits in state
        // or we store the full 8 chars. To keep logic simple, let's store ONLY the digits 
        // in formData.dpId when it's NSDL, and prepend "IN" during DAN generation/submission.
        // Actually, existing logic expects 8 chars. Let's stick to 8 chars but handle the input better.
        
        let val = value.toUpperCase();
        if (val.startsWith("IN")) {
          val = "IN" + val.slice(2).replace(/\D/g, "").slice(0, 6);
        } else {
          val = "IN" + val.replace(/\D/g, "").slice(0, 6);
        }
        
        if (val.length > 8) return;
        setFormData({ ...formData, dpId: val });
        setValidationErrors({ ...validationErrors, dpId: validateField("dpId", val) });
      } else {
        // CDSL: 8 numeric digits only
        const digits = value.replace(/\D/g, "").slice(0, 8);
        setFormData({ ...formData, dpId: digits });
        setValidationErrors({ ...validationErrors, dpId: validateField("dpId", digits) });
      }
      return;
    }

    if (id === "clientId") {
      // 8 numeric digits only, hard blocked
      const digits = value.replace(/\D/g, "").slice(0, 8);
      setFormData({ ...formData, clientId: digits });
      setValidationErrors({ ...validationErrors, clientId: validateField("clientId", digits) });
      return;
    }

    // Update form data
    setFormData({
      ...formData,
      [id]: value,
    });

    // Validate field and update validation errors
    const fieldError = validateField(id, value);
    setValidationErrors({
      ...validationErrors,
      [id]: fieldError,
    });
  };

  const validateForm = () => {
    const errors = {
      dpId: validateField("dpId", formData.dpId),
      clientId: validateField("clientId", formData.clientId),
      depository: validateField("depository", formData.depository),
    };

    setValidationErrors(errors);

    // Return true if no errors (all error messages are empty strings)
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    if (!validateForm()) {
      return;
    }

    // Ensure DAN is generated
    if (!formData.dan) {
      toast.error("DAN could not be generated. Please check your inputs.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submissionData = {
        dpId: formData.dpId,
        clientId: formData.clientId,
        depository: formData.depository,
        dan: formData.dan, // Auto-generated DAN
        kyc: {
          dematAccountDetails: {
            documentType: "CLIENT-MASTER-REPORT",
            flowType: "DAN",
            idNumber: formData.dan,
            metadata: {
              kycItemData: {
                depository: formData.depository,
                dpId: formData.dpId,
                clientId: formData.clientId,
              },
            },
          },
        },
      };

      await kycService.processStep(2, submissionData);
      toast.success("Demat details updated successfully");

      if (typeof onComplete === "function") {
        onComplete();
      } else {
        navigate("/kyc");
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to update demat details";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Lock state ────────────────────────────────────────────────────────────────
  const LOCKED_STATUSES = ["pending", "verified", "in-review"];
  const isLocked = LOCKED_STATUSES.includes(kycStatus);
  const statusBannerConfig = {
    pending: { bg: "bg-yellow-50 border-yellow-300", icon: "⏳", text: "Your KYC is under review.", sub: "Editing is disabled until admin completes the review." },
    verified: { bg: "bg-green-50 border-green-400", icon: "✅", text: "Your KYC is verified.", sub: "No changes are allowed." },
    "in-review": { bg: "bg-blue-50 border-blue-400", icon: "🔍", text: "Your KYC is being reviewed.", sub: "Editing is temporarily locked." },
  };
  const banner = statusBannerConfig[kycStatus];
  // ────────────────────────────────────────────────────────────────

  return (
    <div className="flex justify-center px-4 sm:px-6 lg:px-8 mt-15">
      <div className="bg-white rounded-xl shadow-md w-full max-w-md p-6 sm:p-8 mt-6 relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-blue-600 text-center sm:text-left mb-4 sm:mb-0">
            Demat Account Details
          </h2>
        </div>

        {/* Lock Banner */}
        {isLocked && banner && (
          <div className={`mb-6 flex items-start gap-3 border rounded-xl p-4 ${banner.bg}`}>
            <span className="text-2xl">{banner.icon}</span>
            <div>
              <p className="font-semibold text-gray-800">{banner.text}</p>
              <p className="text-sm text-gray-600 mt-0.5">{banner.sub}</p>
            </div>
          </div>
        )}

        {isLocked ? (
          /* Read-only view */
          <div className="space-y-4">
            {[
              { label: "Depository", value: formData.depository },
              { label: "DP ID", value: formData.dpId },
              { label: "Client ID (BO ID)", value: formData.clientId },
              { label: "DAN (Demat Account Number)", value: formData.dan },
            ].map(({ label, value }) => (
              <div key={label}>
                <label className="block text-gray-500 text-sm font-medium mb-1">{label}</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 min-h-[38px]">
                  {value || <span className="text-gray-400 italic">Not provided</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Editable form */
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Depository Selection */}
              <div>
                <label htmlFor="depository" className="block text-gray-700 font-medium mb-1">
                  Depository *
                </label>
                <select
                  id="depository"
                  value={formData.depository}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border ${
                    validationErrors.depository ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400`}
                >
                  <option value="NSDL">NSDL (National Securities Depository Limited)</option>
                  <option value="CDSL">CDSL (Central Depository Services Limited)</option>
                </select>
                {validationErrors.depository && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.depository}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Select your depository provider</p>
              </div>

              {/* DP ID Input */}
              <div>
                <label htmlFor="dpId" className="block text-gray-700 font-medium mb-1">
                  DP ID *
                </label>
                <div className="relative flex items-center">
                  {formData.depository === "NSDL" && (
                    <div className="absolute left-3 flex items-center pointer-events-none">
                      <span className="text-gray-900 font-bold bg-gray-100 px-2 py-1 rounded border border-gray-300">IN</span>
                    </div>
                  )}
                  <input
                    id="dpId"
                    type="text"
                    placeholder={formData.depository === "NSDL" ? "300123" : "12345678"}
                    value={formData.depository === "NSDL" ? formData.dpId.slice(2) : formData.dpId}
                    onChange={(e) => {
                      if (formData.depository === "NSDL") {
                        // When user types, we take their input and prepend IN back for state
                        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                        handleChange({ target: { id: "dpId", value: "IN" + val } });
                      } else {
                        handleChange(e);
                      }
                    }}
                    required
                    maxLength={formData.depository === "NSDL" ? 6 : 8}
                    className={`w-full px-3 py-2 border ${
                      validationErrors.dpId ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                      formData.depository === "NSDL" ? "pl-14" : ""
                    }`}
                  />
                </div>
                {validationErrors.dpId && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.dpId}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.depository === "NSDL"
                    ? 'NSDL DP ID starts with "IN" (already added) + 6-digit code'
                    : "CDSL DP ID: 8-digit numeric code"}
                </p>
              </div>

              {/* Client ID (BO ID) Input */}
              <div>
                <label htmlFor="clientId" className="block text-gray-700 font-medium mb-1">
                  Client ID (BO ID) *
                </label>
                <input
                  id="clientId"
                  type="text"
                  placeholder="12345678"
                  value={formData.clientId}
                  onChange={handleChange}
                  required
                  maxLength={8}
                  inputMode="numeric"
                  className={`w-full px-3 py-2 border ${
                    validationErrors.clientId ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400`}
                />
                {validationErrors.clientId && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.clientId}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Beneficiary Owner ID — 8-digit numeric code</p>
              </div>

              {/* Auto-Generated DAN (Read-Only) */}
              <div>
                <label htmlFor="dan" className="block text-gray-700 font-medium mb-1">
                  DAN (Demat Account Number)
                  <span className="ml-2 text-xs text-green-600 font-normal">(Auto-Generated)</span>
                </label>
                <input
                  id="dan"
                  type="text"
                  value={formData.dan}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.depository === "NSDL"
                    ? "NSDL DAN = DP ID (IN + 6 digits) + BO ID (8 digits) = 16 characters"
                    : "CDSL DAN = DP ID (8 digits) + Client ID (8 digits) = 16 digits"}
                </p>
                {!formData.dan && (formData.dpId || formData.clientId) && (
                  <p className="mt-1 text-xs text-orange-500">Complete both DP ID and Client ID to generate DAN</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={loading || !formData.dan}
                className={`w-full px-4 py-2 ${
                  loading || !formData.dan ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                } text-white font-semibold rounded-md transition focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {loading ? "Uploading..." : "Upload Demat Details"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DematDetailsForm;
